<script>
import AutomationActionTeamMessageInput from './AutomationActionTeamMessageInput.vue';
import AutomationActionFileInput from './AutomationFileInput.vue';
import AutomationSendMessageInput from './AutomationSendMessageInput.vue';
import TeamAssignmentActionInput from './TeamAssignmentActionInput.vue';
import DateTimeWindowActionInput from './DateTimeWindowActionInput.vue';
import UpdateContactTagActionInput from './UpdateContactTagActionInput.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import AgentAssignmentSelect from 'dashboard/components-next/agent/AgentAssignmentSelect.vue';
import OptionSelect from 'dashboard/components-next/select/OptionSelect.vue';
import SingleSelect from 'dashboard/components-next/filter/inputs/SingleSelect.vue';
import MultiSelect from 'dashboard/components-next/filter/inputs/MultiSelect.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';

export default {
  components: {
    AutomationActionTeamMessageInput,
    AutomationActionFileInput,
    AutomationSendMessageInput,
    TeamAssignmentActionInput,
    DateTimeWindowActionInput,
    UpdateContactTagActionInput,
    WootMessageEditor,
    AgentAssignmentSelect,
    OptionSelect,
    SingleSelect,
    MultiSelect,
    NextInput,
  },
  props: {
    modelValue: {
      type: Object,
      default: () => null,
    },
    actionTypes: {
      type: Array,
      default: () => [],
    },
    dropdownValues: {
      type: Array,
      default: () => [],
    },
    errorMessage: {
      type: String,
      default: '',
    },
    showActionInput: {
      type: Boolean,
      default: true,
    },
    initialFileName: {
      type: String,
      default: '',
    },
    dropdownMaxHeight: {
      type: String,
      default: 'max-h-80',
    },
    isReadOnly: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue', 'input', 'resetAction'],
  computed: {
    action_name: {
      get() {
        if (!this.modelValue) return null;
        return this.modelValue.action_name;
      },
      set(value) {
        const payload = this.modelValue || {};
        this.$emit('update:modelValue', { ...payload, action_name: value });
        this.$emit('input', { ...payload, action_name: value });
      },
    },
    action_params: {
      get() {
        if (!this.modelValue) return null;
        return this.modelValue.action_params;
      },
      set(value) {
        const payload = this.modelValue || {};
        this.$emit('update:modelValue', { ...payload, action_params: value });
        this.$emit('input', { ...payload, action_params: value });
      },
    },
    inputType() {
      return this.actionTypes.find(action => action.key === this.action_name)
        ?.inputType;
    },
    actionNameAsSelectModel: {
      get() {
        if (this.action_name === 'assign_ai_agent') {
          return 'assign_agent';
        }
        return this.action_name || '';
      },
      set(value) {
        this.action_name = value;
      },
    },
    actionInputModel: {
      get() {
        if (Array.isArray(this.action_params)) {
          return this.action_params[0] || null;
        }
        return this.action_params;
      },
      set(value) {
        if (
          this.actionNameAsSelectModel === 'assign_agent' &&
          value?.id?.startsWith?.('ai_agent:')
        ) {
          this.action_name = 'assign_ai_agent';
        } else if (this.actionNameAsSelectModel === 'assign_agent') {
          this.action_name = 'assign_agent';
        }

        this.action_params = value;
      },
    },
    actionTypesAsOptions() {
      return this.actionTypes
        .filter(a => a.key !== 'assign_ai_agent')
        .map(a => ({
          key: a.key,
          label: a.label,
          disabled: a.disabled,
        }));
    },
    isVerticalLayout() {
      return ['team_message', 'textarea'].includes(this.inputType);
    },
    isAgentAssignmentAction() {
      return ['assign_agent', 'assign_ai_agent'].includes(this.action_name);
    },
    isTeamAssignmentAction() {
      return this.action_name === 'assign_team';
    },
    isDateTimeWindowAction() {
      return this.action_name === 'date_time_window';
    },
    isSendMessageAction() {
      return this.action_name === 'send_message';
    },
    isPrivateNoteAction() {
      return this.action_name === 'add_private_note';
    },
    isContactTagAction() {
      return this.action_name === 'update_contact_tag';
    },
    shouldShowActionSelector() {
      return ![
        this.isDateTimeWindowAction,
        this.isSendMessageAction,
        this.isPrivateNoteAction,
        this.isContactTagAction,
      ].some(Boolean);
    },
    shouldShowInlineError() {
      return (
        Boolean(this.errorMessage) &&
        !this.isTeamAssignmentAction &&
        !this.isDateTimeWindowAction &&
        !this.isContactTagAction
      );
    },
    actionInputDropdownMaxHeight() {
      if (this.isAgentAssignmentAction || this.isTeamAssignmentAction) {
        return 'max-h-[18rem]';
      }

      return this.dropdownMaxHeight;
    },
    castMessageVmodel: {
      get() {
        if (Array.isArray(this.action_params)) {
          return this.action_params[0];
        }
        return this.action_params;
      },
      set(value) {
        this.action_params = value;
      },
    },
  },
  methods: {
    resetAction() {
      this.$emit('resetAction');
    },
    onActionNameChange(value) {
      this.actionNameAsSelectModel = value;
      this.resetAction();
    },
  },
};
</script>

<template>
  <li class="flex h-full min-h-0 list-none py-2 first:pt-0 last:pb-0">
    <div
      class="flex h-full min-h-0 flex-1 flex-col gap-4"
      :class="{ 'animate-wiggle': errorMessage }"
    >
      <div v-if="shouldShowActionSelector" class="flex flex-col gap-1">
        <label class="text-xs font-medium leading-4 text-n-slate-11">
          {{ $t('AUTOMATION.ADD.FORM.ACTIONS.LABEL') }}
        </label>
        <OptionSelect
          :model-value="actionNameAsSelectModel"
          :options="actionTypesAsOptions"
          :dropdown-max-height="dropdownMaxHeight"
          :disabled="isReadOnly"
          :aria-label="$t('AUTOMATION.ADD.FORM.ACTIONS.LABEL')"
          class="w-full"
          @update:model-value="onActionNameChange"
        />
      </div>
      <div
        v-if="showActionInput && !isVerticalLayout"
        class="flex min-h-0 flex-1 w-full flex-col gap-1"
      >
        <label
          v-if="
            isAgentAssignmentAction ||
            isTeamAssignmentAction ||
            isDateTimeWindowAction
          "
          class="text-xs font-medium leading-4 text-n-slate-11"
        >
          {{
            isDateTimeWindowAction
              ? $t('AUTOMATION.ACTIONS.DATE_TIME_WINDOW')
              : $t('AUTOMATION.FLOW.ADD_STEPS.LABELS.ASSIGN_TO')
          }}
        </label>
        <AgentAssignmentSelect
          v-if="inputType === 'search_select' && isAgentAssignmentAction"
          v-model="actionInputModel"
          :options="dropdownValues"
          :dropdown-max-height="actionInputDropdownMaxHeight"
          :disabled="isReadOnly"
          :aria-label="$t('AUTOMATION.ADD.FORM.ACTIONS.LABEL')"
          class="flex-1"
        />
        <TeamAssignmentActionInput
          v-else-if="inputType === 'search_select' && isTeamAssignmentAction"
          v-model="actionInputModel"
          :teams="dropdownValues"
          :dropdown-max-height="actionInputDropdownMaxHeight"
          :disabled="isReadOnly"
          :error-message="errorMessage"
          class="min-h-0 flex-1"
        />
        <DateTimeWindowActionInput
          v-else-if="inputType === 'date_time_window'"
          v-model="actionInputModel"
          :disabled="isReadOnly"
          :error-message="errorMessage"
          class="flex-1"
        />
        <UpdateContactTagActionInput
          v-else-if="inputType === 'contact_tag'"
          v-model="actionInputModel"
          :options="dropdownValues"
          :disabled="isReadOnly"
          :error-message="errorMessage"
          class="flex-1"
        />
        <SingleSelect
          v-else-if="inputType === 'search_select'"
          v-model="actionInputModel"
          :options="dropdownValues"
          :dropdown-max-height="actionInputDropdownMaxHeight"
          class="flex-1"
        />
        <MultiSelect
          v-else-if="inputType === 'multi_select'"
          v-model="action_params"
          :options="dropdownValues"
          :dropdown-max-height="actionInputDropdownMaxHeight"
          class="flex-1"
        />
        <NextInput
          v-else-if="inputType === 'email'"
          v-model="action_params"
          type="email"
          size="sm"
          :disabled="isReadOnly"
          :placeholder="$t('AUTOMATION.ACTION.EMAIL_INPUT_PLACEHOLDER')"
          class="flex-1"
        />
        <NextInput
          v-else-if="inputType === 'url'"
          v-model="action_params"
          type="url"
          size="sm"
          :disabled="isReadOnly"
          :placeholder="$t('AUTOMATION.ACTION.URL_INPUT_PLACEHOLDER')"
          class="flex-1"
        />
        <AutomationActionFileInput
          v-else-if="inputType === 'attachment'"
          v-model="action_params"
          :initial-file-name="initialFileName"
          class="flex-1"
        />
      </div>
      <AutomationActionTeamMessageInput
        v-if="inputType === 'team_message'"
        v-model="action_params"
        :teams="dropdownValues"
        :dropdown-max-height="dropdownMaxHeight"
      />
      <AutomationSendMessageInput
        v-else-if="
          inputType === 'textarea' &&
          (isSendMessageAction || isPrivateNoteAction)
        "
        v-model="castMessageVmodel"
        :disabled="isReadOnly"
        :show-message-type="isSendMessageAction"
        :input-label="
          isPrivateNoteAction ? $t('AUTOMATION.ACTION.COMMENT_LABEL') : ''
        "
      />
      <WootMessageEditor
        v-else-if="inputType === 'textarea'"
        v-model="castMessageVmodel"
        rows="4"
        :disabled="isReadOnly"
        :is-private="isPrivateNoteAction"
        enable-variables
        :placeholder="$t('AUTOMATION.ACTION.TEAM_MESSAGE_INPUT_PLACEHOLDER')"
        class="[&_.ProseMirror-menubar]:hidden px-3 py-1 bg-n-alpha-1 rounded-lg outline outline-1 outline-n-weak dark:outline-n-strong"
      />
    </div>
    <span v-if="shouldShowInlineError" class="text-sm text-n-ruby-11">
      {{ errorMessage }}
    </span>
  </li>
</template>
